/**
 * Stream B — B39: Social Publishing (Multi-Platform)
 * SA-E: Save Draft, Reschedule, Cancel UI controls wired to existing backend.
 *
 * Compose post, select platforms, preview, schedule/publish.
 * Real apiFetch. No mocks. CSS variables only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import {
  Send,
  Calendar,
  Image as ImageIcon,
  Globe,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  RefreshCw,
  XCircle,
} from "lucide-react";

interface ConnectedAccount {
  platform: string;
  accountName: string;
  connected: boolean;
  characterLimit: number;
}

interface CalendarAccountsResponse {
  platformAccounts: Array<{ platform: string; accountName: string; connected: boolean }>;
}

interface ScheduledPostState {
  postId: string;
  content: string;
  platforms: string[];
  scheduledAt: string;
}

const PLATFORM_CHARACTER_LIMITS: Record<string, number> = {
  facebook: 5000,
  instagram: 2200,
  linkedin: 3000,
};

function getWeekStart(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0] ?? "";
}

function toCalendarAccounts(data: CalendarAccountsResponse): ConnectedAccount[] {
  return data.platformAccounts.map((account) => ({
    ...account,
    characterLimit: PLATFORM_CHARACTER_LIMITS[account.platform] ?? 280,
  }));
}

function toScheduledIso(date: string, time: string): string | undefined {
  if (!date || !time) return undefined;
  return new Date(`${date}T${time}:00`).toISOString();
}

function splitIso(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const yyyy = d.getFullYear().toString().padStart(4, "0");
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  const hh = d.getHours().toString().padStart(2, "0");
  const mi = d.getMinutes().toString().padStart(2, "0");
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

export default function SocialPublishingPage() {
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set());
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isMutatingScheduled, setIsMutatingScheduled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [scheduledPost, setScheduledPost] = useState<ScheduledPostState | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");

  const fetchAccounts = useCallback(async () => {
    try {
      const weekStart = getWeekStart(new Date());
      const res = await apiFetch<{ data: CalendarAccountsResponse }>(`/crm/social/calendar?week=${weekStart}`);
      setAccounts(toCalendarAccounts(res.data));
    } catch {
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  const resetComposer = () => {
    setContent("");
    setSelectedPlatforms(new Set());
    setScheduleDate("");
    setScheduleTime("");
  };

  const handlePublish = async (isScheduled: boolean) => {
    if (!content.trim() || selectedPlatforms.size === 0) return;
    setIsPublishing(true);
    setError(null);
    setSuccess(null);
    try {
      const scheduledAt = isScheduled ? toScheduledIso(scheduleDate, scheduleTime) : undefined;
      const trimmedContent = content.trim();
      const platforms = Array.from(selectedPlatforms);
      const created = await apiFetch<{ data: { postId: string } }>("/crm/social/calendar", {
        method: "POST",
        body: JSON.stringify({
          content: trimmedContent,
          platforms,
          ...(scheduledAt ? { scheduledAt } : {}),
        }),
      });
      if (!scheduledAt) {
        await apiFetch(`/crm/social/calendar/${created.data.postId}/publish`, { method: "POST" });
      } else {
        const split = splitIso(scheduledAt);
        setScheduledPost({ postId: created.data.postId, content: trimmedContent, platforms, scheduledAt });
        setRescheduleDate(split.date);
        setRescheduleTime(split.time);
      }
      setSuccess(isScheduled ? "Post scheduled successfully" : "Post published successfully");
      resetComposer();
    } catch {
      setError("Failed to publish. Check your connected accounts.");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!content.trim() || selectedPlatforms.size === 0) return;
    setIsSavingDraft(true);
    setError(null);
    setSuccess(null);
    try {
      await apiFetch<{ data: { postId: string } }>("/crm/social/calendar", {
        method: "POST",
        body: JSON.stringify({
          content: content.trim(),
          platforms: Array.from(selectedPlatforms),
        }),
      });
      setSuccess("Draft saved");
      resetComposer();
    } catch {
      setError("Failed to save draft. Check your connected accounts.");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleReschedule = async () => {
    if (!scheduledPost) return;
    const nextScheduledAt = toScheduledIso(rescheduleDate, rescheduleTime);
    if (!nextScheduledAt) return;
    setIsMutatingScheduled(true);
    setError(null);
    setSuccess(null);
    try {
      await apiFetch(`/crm/social/calendar/${scheduledPost.postId}`, {
        method: "PATCH",
        body: JSON.stringify({ scheduledAt: nextScheduledAt }),
      });
      setScheduledPost({ ...scheduledPost, scheduledAt: nextScheduledAt });
      setSuccess("Post rescheduled");
    } catch {
      setError("Failed to reschedule. Try again.");
    } finally {
      setIsMutatingScheduled(false);
    }
  };

  const handleCancelScheduled = async () => {
    if (!scheduledPost) return;
    setIsMutatingScheduled(true);
    setError(null);
    setSuccess(null);
    try {
      await apiFetch(`/crm/social/calendar/${scheduledPost.postId}`, { method: "DELETE" });
      setScheduledPost(null);
      setRescheduleDate("");
      setRescheduleTime("");
      setSuccess("Scheduled post cancelled");
    } catch {
      setError("Failed to cancel. Try again.");
    } finally {
      setIsMutatingScheduled(false);
    }
  };

  const connectedAccounts = accounts.filter((a) => a.connected);
  const minCharLimit = connectedAccounts
    .filter((a) => selectedPlatforms.has(a.platform))
    .reduce((min, a) => Math.min(min, a.characterLimit), Infinity);
  const isOverLimit = minCharLimit !== Infinity && content.length > minCharLimit;
  const isComposerEmpty = !content.trim() || selectedPlatforms.size === 0;
  const isAnyActionRunning = isPublishing || isSavingDraft;

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-[var(--color-surface)]" />
        <div className="h-48 animate-pulse rounded-xl bg-[var(--color-surface)]" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Send className="h-5 w-5 text-[var(--color-primary)]" />
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Publish to Social</h1>
      </div>

      {error && (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-3 text-sm text-[var(--color-success)] flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" /> {success}
        </div>
      )}

      {connectedAccounts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
          <Globe className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
          <p className="font-medium text-[var(--color-foreground)]">No social accounts connected</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Connect your accounts in Settings to start publishing.</p>
        </div>
      ) : (
        <>
          {/* Platform selection */}
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <h3 className="text-sm font-medium text-[var(--color-muted-foreground)] mb-3">Publish to</h3>
            <div className="flex flex-wrap gap-2">
              {connectedAccounts.map((a) => (
                <button
                  type="button"
                  key={a.platform}
                  onClick={() => togglePlatform(a.platform)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    selectedPlatforms.has(a.platform)
                      ? "bg-[var(--color-primary)] text-white"
                      : "bg-[var(--color-surface)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                  }`}
                >
                  {a.platform} ({a.accountName})
                </button>
              ))}
            </div>
          </div>

          {/* Compose */}
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What's on your mind?"
              rows={5}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
            />
            <div className="mt-2 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <button type="button" className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                  <ImageIcon className="h-4 w-4" />
                </button>
              </div>
              <span className={`${isOverLimit ? "text-[var(--color-error)]" : "text-[var(--color-muted-foreground)]"}`} style={{ fontVariantNumeric: "tabular-nums" }}>
                {content.length}{minCharLimit !== Infinity ? ` / ${minCharLimit}` : ""}
              </span>
            </div>
          </div>

          {isOverLimit && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-error)]">
              <AlertCircle className="h-3.5 w-3.5" />
              Content exceeds character limit for selected platform(s)
            </div>
          )}

          {/* Schedule */}
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <h3 className="text-sm font-medium text-[var(--color-muted-foreground)] mb-3 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> Schedule (optional)
            </h3>
            <div className="flex gap-3">
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {scheduleDate && scheduleTime ? (
              <button
                type="button"
                onClick={() => handlePublish(true)}
                disabled={isAnyActionRunning || isComposerEmpty || isOverLimit}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                <Calendar className="h-4 w-4" />
                {isPublishing ? "Scheduling..." : "Schedule Post"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handlePublish(false)}
                disabled={isAnyActionRunning || isComposerEmpty || isOverLimit}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {isPublishing ? "Publishing..." : "Publish Now"}
              </button>
            )}
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isAnyActionRunning || isComposerEmpty || isOverLimit}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-5 py-2 text-sm font-medium text-[var(--color-foreground)] hover:opacity-90 disabled:opacity-50"
            >
              <FileText className="h-4 w-4" />
              {isSavingDraft ? "Saving..." : "Save Draft"}
            </button>
          </div>

          {/* Manage last scheduled post */}
          {scheduledPost && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium text-[var(--color-foreground)] flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" /> Manage scheduled post
                </h3>
                <span className="text-xs text-[var(--color-muted-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {new Date(scheduledPost.scheduledAt).toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)] line-clamp-2">{scheduledPost.content}</p>
              <div className="flex flex-wrap gap-3">
                <input
                  type="date"
                  aria-label="Reschedule date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                />
                <input
                  type="time"
                  aria-label="Reschedule time"
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleReschedule}
                  disabled={isMutatingScheduled || !rescheduleDate || !rescheduleTime}
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  {isMutatingScheduled ? "Saving..." : "Reschedule"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelScheduled}
                  disabled={isMutatingScheduled}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-background)] px-4 py-2 text-sm font-medium text-[var(--color-error)] hover:opacity-90 disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  Cancel post
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
